import { useTranslation } from "react-i18next";
import {
    ShieldCheckIcon,
    SignalIcon,
    EnvelopeIcon,
    LockClosedIcon,
    BugAntIcon,
    EyeIcon,
} from "@heroicons/react/20/solid";
import { Heading, Subheading } from "src/components/base/heading";
import { Text } from "src/components/base/text";
import { Badge } from "src/components/base/badge";
import { Table, TableHead, TableBody, TableRow, TableHeader, TableCell } from "src/components/base/table";
import type { View } from "../types";
import { VIEW_COLORS, VIEW_LABELS } from "../types";
import type { UnifiedEvent } from "../data/events";
import { UNIFIED_EVENTS } from "../data/events";
import { StatCard, Card, HBar, Sparkline, FeedEntry, CorrelationBadges, IncidentChain, SectionTitle } from "../shared";

/* ── User Risk Scores ─────────────────────────────────────────────── */
function UserRiskScores() {
    const users = [
        { name: "M. Hoffmann", dept: "Sales", clickRate: 42, training: "Overdue", status: "red" as const },
        { name: "S. Braun", dept: "HR", clickRate: 38, training: "Overdue", status: "red" as const },
        { name: "K. Meyer", dept: "Finance", clickRate: 33, training: "In Progress", status: "amber" as const },
        { name: "J. Klein", dept: "Sales", clickRate: 29, training: "In Progress", status: "amber" as const },
        { name: "P. Wagner", dept: "Marketing", clickRate: 25, training: "Overdue", status: "red" as const },
        { name: "A. Becker", dept: "Sales", clickRate: 21, training: "Completed", status: "green" as const },
        { name: "T. Schulz", dept: "HR", clickRate: 18, training: "In Progress", status: "amber" as const },
        { name: "L. Weber", dept: "Finance", clickRate: 15, training: "Completed", status: "green" as const },
        { name: "F. Richter", dept: "Marketing", clickRate: 12, training: "Completed", status: "green" as const },
        { name: "C. Wolf", dept: "IT", clickRate: 8, training: "Completed", status: "green" as const },
    ];
    const maxRate = users[0].clickRate;
    return (
        <div className="space-y-2">
            {users.map((u, i) => (
                <div
                    key={u.name}
                    className="flex items-center gap-3 rounded-lg border border-zinc-200/30 bg-white/30 px-3 py-2 backdrop-blur-sm transition-all hover:bg-white/50 dark:border-zinc-700/30 dark:bg-zinc-800/30 dark:hover:bg-zinc-800/50"
                >
                    <span className="w-5 text-right text-[10px] font-bold text-zinc-400">#{i + 1}</span>
                    <div className="flex h-7 w-7 items-center justify-center rounded-full bg-gradient-to-br from-orange-500/20 to-red-500/20 text-[10px] font-bold text-orange-600 dark:text-orange-400">
                        {u.name
                            .split(" ")
                            .map((n) => n[0])
                            .join("")}
                    </div>
                    <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                            <span className="text-xs font-semibold text-zinc-700 dark:text-zinc-300">{u.name}</span>
                            <span className="text-[10px] text-zinc-400">{u.dept}</span>
                        </div>
                        <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-zinc-100/80 dark:bg-zinc-800/80">
                            <div
                                className="h-1.5 rounded-full bg-gradient-to-r from-orange-500 to-red-500 transition-all"
                                style={{ width: `${(u.clickRate / maxRate) * 100}%` }}
                            />
                        </div>
                    </div>
                    <span className="w-10 text-right font-mono text-xs font-bold text-zinc-600 dark:text-zinc-400">
                        {u.clickRate}%
                    </span>
                    <Badge color={u.status}>{u.training}</Badge>
                </div>
            ))}
        </div>
    );
}

/* ── Campaign Comparison Radar ────────────────────────────────────── */
function CampaignRadar() {
    const axes = ["Click Rate", "Report Rate", "Open Rate", "Response Time", "Awareness"];
    const campaigns = [
        { name: "IT Helpdesk", values: [0.76, 0.45, 0.88, 0.62, 0.71], color: "#3b82f6" },
        { name: "DHL Paket", values: [0.85, 0.32, 0.92, 0.48, 0.55], color: "#f97316" },
        { name: "CEO Fraud", values: [0.51, 0.68, 0.74, 0.82, 0.78], color: "#8b5cf6" },
    ];
    const cx = 150,
        cy = 130,
        maxR = 95;
    const angleStep = (2 * Math.PI) / axes.length;

    const toPoint = (i: number, val: number) => ({
        x: cx + maxR * val * Math.cos(i * angleStep - Math.PI / 2),
        y: cy + maxR * val * Math.sin(i * angleStep - Math.PI / 2),
    });

    return (
        <div className="flex flex-col items-center gap-4">
            <svg viewBox="0 0 300 270" className="w-full max-w-[350px]">
                <defs>
                    {campaigns.map((c) => (
                        <linearGradient
                            key={c.name}
                            id={`radar-${c.name.replace(/\s/g, "")}`}
                            x1="0"
                            y1="0"
                            x2="0"
                            y2="1"
                        >
                            <stop offset="0%" stopColor={c.color} stopOpacity="0.25" />
                            <stop offset="100%" stopColor={c.color} stopOpacity="0.05" />
                        </linearGradient>
                    ))}
                </defs>
                {/* Grid rings */}
                {[0.2, 0.4, 0.6, 0.8, 1.0].map((ring) => (
                    <polygon
                        key={ring}
                        points={axes
                            .map((_, i) => {
                                const p = toPoint(i, ring);
                                return `${p.x},${p.y}`;
                            })
                            .join(" ")}
                        fill="none"
                        stroke="rgba(59,130,246,0.08)"
                        strokeWidth="1"
                    />
                ))}
                {/* Axis lines */}
                {axes.map((_, i) => {
                    const p = toPoint(i, 1);
                    return (
                        <line key={i} x1={cx} y1={cy} x2={p.x} y2={p.y} stroke="rgba(59,130,246,0.1)" strokeWidth="1" />
                    );
                })}
                {/* Axis labels */}
                {axes.map((label, i) => {
                    const p = toPoint(i, 1.18);
                    return (
                        <text
                            key={label}
                            x={p.x}
                            y={p.y}
                            textAnchor="middle"
                            dominantBaseline="middle"
                            className="fill-zinc-500"
                            fontSize="7"
                            fontWeight="600"
                        >
                            {label}
                        </text>
                    );
                })}
                {/* Campaign polygons */}
                {campaigns.map((c) => {
                    const pts = c.values.map((v, i) => toPoint(i, v));
                    return (
                        <g key={c.name}>
                            <polygon
                                points={pts.map((p) => `${p.x},${p.y}`).join(" ")}
                                fill={`url(#radar-${c.name.replace(/\s/g, "")})`}
                                stroke={c.color}
                                strokeWidth="1.5"
                                strokeLinejoin="round"
                            />
                            {pts.map((p, i) => (
                                <circle key={i} cx={p.x} cy={p.y} r="2.5" fill={c.color} />
                            ))}
                        </g>
                    );
                })}
            </svg>
            <div className="flex gap-4">
                {campaigns.map((c) => (
                    <div key={c.name} className="flex items-center gap-1.5 text-xs text-zinc-600 dark:text-zinc-400">
                        <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ backgroundColor: c.color }} />
                        {c.name}
                    </div>
                ))}
            </div>
        </div>
    );
}

/* ── Training Progress ────────────────────────────────────────────── */
function TrainingProgress() {
    const departments = [
        { name: "Engineering", progress: 95, color: "from-emerald-500 to-emerald-600" },
        { name: "IT", progress: 92, color: "from-blue-500 to-blue-600" },
        { name: "Finance", progress: 88, color: "from-blue-400 to-blue-500" },
        { name: "HR", progress: 81, color: "from-amber-500 to-amber-600" },
        { name: "Sales", progress: 74, color: "from-orange-500 to-orange-600" },
    ];
    return (
        <div className="space-y-3.5">
            {departments.map((d) => (
                <div key={d.name}>
                    <div className="flex items-center justify-between text-xs">
                        <span className="font-semibold text-zinc-700 dark:text-zinc-300">{d.name}</span>
                        <span className="font-mono font-bold text-zinc-600 dark:text-zinc-400">{d.progress}%</span>
                    </div>
                    <div className="mt-1.5 h-3 w-full overflow-hidden rounded-full bg-zinc-100/80 dark:bg-zinc-800/80">
                        <div
                            className={`h-3 rounded-full bg-gradient-to-r ${d.color} transition-all duration-700`}
                            style={{ width: `${d.progress}%`, boxShadow: "0 0 8px rgba(59,130,246,0.2)" }}
                        />
                    </div>
                </div>
            ))}
        </div>
    );
}

/* ── Social Engineering Vectors (Donut) ───────────────────────────── */
function SocialEngVectors() {
    const vectors = [
        { label: "Email", pct: 62, color: "#3b82f6" },
        { label: "SMS", pct: 18, color: "#f97316" },
        { label: "Phone", pct: 12, color: "#8b5cf6" },
        { label: "USB", pct: 8, color: "#ef4444" },
    ];
    let cum = 0;
    const total = vectors.reduce((s, v) => s + v.pct, 0);
    const gradientStops = vectors.flatMap((v) => {
        const start = (cum / total) * 360;
        cum += v.pct;
        const end = (cum / total) * 360;
        return [`${v.color} ${start}deg ${end}deg`];
    });
    return (
        <div className="flex flex-col items-center gap-4">
            <div
                className="relative rounded-full shadow-lg shadow-blue-500/10"
                style={{ width: 140, height: 140, background: `conic-gradient(${gradientStops.join(", ")})` }}
            >
                <div
                    className="absolute inset-0 m-auto rounded-full bg-white/90 backdrop-blur-sm dark:bg-zinc-900/90"
                    style={{ width: 80, height: 80 }}
                />
                <span className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className="text-lg font-extrabold text-zinc-700 dark:text-zinc-200">4</span>
                    <span className="text-[8px] text-zinc-400">Vectors</span>
                </span>
            </div>
            <div className="flex flex-wrap justify-center gap-x-3 gap-y-1.5">
                {vectors.map((v) => (
                    <div key={v.label} className="flex items-center gap-1.5 text-xs text-zinc-600 dark:text-zinc-400">
                        <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ backgroundColor: v.color }} />
                        {v.label} ({v.pct}%)
                    </div>
                ))}
            </div>
        </div>
    );
}

/* ── Simulation Calendar ──────────────────────────────────────────── */
function SimulationCalendar() {
    // Feb 2026 calendar mock
    const weeks: { day: number; hasCampaign: boolean; campaignColor?: string }[][] = [
        [
            { day: 0, hasCampaign: false },
            { day: 0, hasCampaign: false },
            { day: 0, hasCampaign: false },
            { day: 0, hasCampaign: false },
            { day: 0, hasCampaign: false },
            { day: 0, hasCampaign: false },
            { day: 1, hasCampaign: false },
        ],
        [
            { day: 2, hasCampaign: true, campaignColor: "#3b82f6" },
            { day: 3, hasCampaign: false },
            { day: 4, hasCampaign: false },
            { day: 5, hasCampaign: false },
            { day: 6, hasCampaign: false },
            { day: 7, hasCampaign: false },
            { day: 8, hasCampaign: false },
        ],
        [
            { day: 9, hasCampaign: false },
            { day: 10, hasCampaign: true, campaignColor: "#f97316" },
            { day: 11, hasCampaign: true, campaignColor: "#f97316" },
            { day: 12, hasCampaign: false },
            { day: 13, hasCampaign: false },
            { day: 14, hasCampaign: false },
            { day: 15, hasCampaign: false },
        ],
        [
            { day: 16, hasCampaign: false },
            { day: 17, hasCampaign: false },
            { day: 18, hasCampaign: true, campaignColor: "#8b5cf6" },
            { day: 19, hasCampaign: true, campaignColor: "#8b5cf6" },
            { day: 20, hasCampaign: true, campaignColor: "#8b5cf6" },
            { day: 21, hasCampaign: false },
            { day: 22, hasCampaign: false },
        ],
        [
            { day: 23, hasCampaign: false },
            { day: 24, hasCampaign: false },
            { day: 25, hasCampaign: false },
            { day: 26, hasCampaign: true, campaignColor: "#3b82f6" },
            { day: 27, hasCampaign: false },
            { day: 28, hasCampaign: false },
            { day: 0, hasCampaign: false },
        ],
    ];
    const dayLabels = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
    return (
        <div>
            <p className="mb-2 text-xs font-semibold text-zinc-500">February 2026</p>
            <div className="grid grid-cols-7 gap-1">
                {dayLabels.map((d) => (
                    <div key={d} className="pb-1 text-center text-[8px] font-semibold text-zinc-400">
                        {d}
                    </div>
                ))}
                {weeks.flat().map((cell, i) => (
                    <div
                        key={i}
                        className={`relative flex h-8 items-center justify-center rounded-md text-[10px] transition-all ${cell.day === 0 ? "text-zinc-300 dark:text-zinc-700" : "text-zinc-600 dark:text-zinc-400"} ${cell.day > 0 ? "border border-zinc-200/30 bg-white/30 hover:bg-white/60 dark:border-zinc-700/30 dark:bg-zinc-800/30 dark:hover:bg-zinc-800/60" : ""}`}
                    >
                        {cell.day > 0 && cell.day}
                        {cell.hasCampaign && cell.day > 0 && (
                            <div
                                className="absolute bottom-1 h-1.5 w-1.5 rounded-full"
                                style={{ backgroundColor: cell.campaignColor }}
                            />
                        )}
                    </div>
                ))}
            </div>
            <div className="mt-3 flex gap-3">
                {[
                    { label: "IT Helpdesk", color: "#3b82f6" },
                    { label: "DHL Paket", color: "#f97316" },
                    { label: "CEO Fraud", color: "#8b5cf6" },
                ].map((c) => (
                    <div key={c.label} className="flex items-center gap-1 text-[9px] text-zinc-500">
                        <span className="inline-block h-2 w-2 rounded-full" style={{ backgroundColor: c.color }} />
                        {c.label}
                    </div>
                ))}
            </div>
        </div>
    );
}

/* ── Benchmark Comparison ─────────────────────────────────────────── */
function BenchmarkComparison() {
    const metrics = [
        { label: "Click Rate", company: 11.2, industry: 18.5 },
        { label: "Report Rate", company: 45.3, industry: 28.1 },
        { label: "Open Rate", company: 62.4, industry: 71.2 },
        { label: "Training Completion", company: 86.0, industry: 72.0 },
        { label: "Repeat Clickers", company: 4.2, industry: 12.8 },
    ];
    const max = 75;
    return (
        <div className="space-y-3.5">
            {metrics.map((m) => (
                <div key={m.label}>
                    <div className="flex items-center justify-between text-xs">
                        <span className="font-semibold text-zinc-700 dark:text-zinc-300">{m.label}</span>
                        <div className="flex gap-3">
                            <span className="font-mono text-blue-600 dark:text-blue-400">{m.company}%</span>
                            <span className="font-mono text-zinc-400">{m.industry}%</span>
                        </div>
                    </div>
                    <div className="mt-1.5 flex gap-1">
                        <div className="h-2.5 flex-1 overflow-hidden rounded-full bg-zinc-100/80 dark:bg-zinc-800/80">
                            <div
                                className="h-2.5 rounded-full bg-gradient-to-r from-blue-500 to-blue-600 transition-all"
                                style={{ width: `${Math.min((m.company / max) * 100, 100)}%` }}
                            />
                        </div>
                        <div className="h-2.5 flex-1 overflow-hidden rounded-full bg-zinc-100/80 dark:bg-zinc-800/80">
                            <div
                                className="h-2.5 rounded-full bg-zinc-300 transition-all dark:bg-zinc-600"
                                style={{ width: `${Math.min((m.industry / max) * 100, 100)}%` }}
                            />
                        </div>
                    </div>
                </div>
            ))}
            <div className="flex gap-4 pt-1">
                <div className="flex items-center gap-1.5 text-[10px] text-zinc-500">
                    <div className="h-2 w-4 rounded-full bg-gradient-to-r from-blue-500 to-blue-600" />
                    Your Company
                </div>
                <div className="flex items-center gap-1.5 text-[10px] text-zinc-500">
                    <div className="h-2 w-4 rounded-full bg-zinc-300 dark:bg-zinc-600" />
                    Industry Average
                </div>
            </div>
        </div>
    );
}

/* ── Main Dashboard ───────────────────────────────────────────────── */
export function PhishingDashboard({
    onNavigate,
    onInvestigate,
}: {
    onNavigate: (v: View) => void;
    onInvestigate: (e: UnifiedEvent) => void;
}) {
    const { t } = useTranslation();
    const phishEvents = UNIFIED_EVENTS.filter((e) => e.system === "phishing");
    const nameMap: Record<string, string> = {
        "PHISH-201": "Q2 — IT Helpdesk",
        "PHISH-198": "Q2 — IT Helpdesk",
        "PHISH-195": "Q1 — DHL Paket",
        "PHISH-192": "Q1 — CEO Fraud",
    };

    return (
        <>
            <Heading>{t("phishing.title")}</Heading>

            {/* ── Stat Cards ──────────────────────────────────────────── */}
            <div className="mt-8 grid grid-cols-2 gap-4 lg:grid-cols-4">
                <StatCard label={t("phishing.activeCampaigns")} value="1" />
                <StatCard label={t("phishing.avgClickRate")} value="11.2%" trend="-3.1% vs. Q4" trendDown />
                <StatCard label={t("phishing.reported")} value="487" />
                <StatCard label={t("phishing.trainedUsers")} value="892" />
            </div>

            {/* ── Original 3-col grid ─────────────────────────────────── */}
            <div className="mt-8 grid gap-6 lg:grid-cols-3">
                <Card>
                    <Subheading>Click Rate by Campaign</Subheading>
                    <div className="mt-4">
                        <HBar
                            items={[
                                { label: "DHL Paket", value: 15.1, max: 20, color: "bg-blue-700 dark:bg-blue-400" },
                                { label: "CEO Fraud", value: 9.1, max: 20, color: "bg-blue-500" },
                                { label: "IT Helpdesk", value: 7.6, max: 20, color: "bg-blue-400" },
                            ]}
                        />
                    </div>
                </Card>
                <Card>
                    <Subheading>Click Rate Trend (%)</Subheading>
                    <div className="mt-4">
                        <Sparkline data={[18.4, 16.2, 14.3, 11.2]} />
                        <div className="mt-2 flex justify-between text-xs text-zinc-400">
                            <span>Q3 &apos;25</span>
                            <span>Q4 &apos;25</span>
                            <span>Q1 &apos;26</span>
                            <span>Q2 &apos;26</span>
                        </div>
                    </div>
                </Card>
                <Card>
                    <Subheading>Cross-System Feed</Subheading>
                    <div className="mt-2 divide-y divide-zinc-950/5 dark:divide-white/5">
                        <FeedEntry
                            time="14:20"
                            icon={EnvelopeIcon}
                            color="bg-blue-400 dark:bg-blue-500"
                            linkedSystem="mail"
                            onNavigate={onNavigate}
                        >
                            12 real phishing caught matching IT Helpdesk
                        </FeedEntry>
                        <FeedEntry
                            time="13:55"
                            icon={ShieldCheckIcon}
                            color="bg-blue-700 dark:bg-blue-400"
                            linkedSystem="soc"
                            onNavigate={onNavigate}
                        >
                            29 clickers flagged — 2 infections found
                        </FeedEntry>
                        <FeedEntry
                            time="13:41"
                            icon={LockClosedIcon}
                            color="bg-blue-500"
                            linkedSystem="ztna"
                            onNavigate={onNavigate}
                        >
                            Trust lowered for 29 clickers, 3 restricted
                        </FeedEntry>
                        <FeedEntry
                            time="12:30"
                            icon={SignalIcon}
                            color="bg-blue-400 dark:bg-blue-500"
                            linkedSystem="ndr"
                            onNavigate={onNavigate}
                        >
                            NDR blocked 5 callbacks from DHL clickers
                        </FeedEntry>
                        <FeedEntry
                            time="09:15"
                            icon={BugAntIcon}
                            color="bg-blue-700 dark:bg-blue-400"
                            linkedSystem="pentests"
                            onNavigate={onNavigate}
                        >
                            PT-001 used same social eng. vector
                        </FeedEntry>
                    </div>
                </Card>
            </div>

            {/* ── User Risk Scores ────────────────────────────────────── */}
            <SectionTitle title="User Risk Scores" description="Top 10 riskiest users by phishing click rate" />
            <Card>
                <UserRiskScores />
            </Card>

            {/* ── Campaign Radar + Social Eng Vectors ─────────────────── */}
            <SectionTitle
                title="Campaign Analysis"
                description="Cross-campaign comparison and attack vector distribution"
            />
            <div className="grid gap-6 lg:grid-cols-2">
                <Card>
                    <Subheading>Campaign Comparison Radar</Subheading>
                    <div className="mt-4">
                        <CampaignRadar />
                    </div>
                </Card>
                <Card>
                    <Subheading>Social Engineering Vectors</Subheading>
                    <div className="mt-4">
                        <SocialEngVectors />
                    </div>
                </Card>
            </div>

            {/* ── Training Progress ────────────────────────────────────── */}
            <SectionTitle
                title="Training Progress"
                description="Security awareness training completion by department"
            />
            <Card>
                <TrainingProgress />
            </Card>

            {/* ── Simulation Calendar + Benchmark ─────────────────────── */}
            <SectionTitle title="Simulations & Benchmarks" description="Campaign schedule and industry comparison" />
            <div className="grid gap-6 lg:grid-cols-2">
                <Card>
                    <Subheading>Simulation Calendar</Subheading>
                    <div className="mt-4">
                        <SimulationCalendar />
                    </div>
                </Card>
                <Card>
                    <Subheading>Industry Benchmark Comparison</Subheading>
                    <div className="mt-4">
                        <BenchmarkComparison />
                    </div>
                </Card>
            </div>

            {/* ── Cross-System Incident Chain (original) ──────────────── */}
            <Card className="mt-6">
                <Subheading>Cross-System Incident Chain</Subheading>
                <div className="mt-4">
                    <Text className="mb-1 text-xs font-medium sm:text-xs">PHISH-Q2: Simulation → Real Attack</Text>
                    <IncidentChain
                        onNavigate={onNavigate}
                        steps={[
                            { system: "phishing", label: "Sim sent" },
                            { system: "mail", label: "Real attack" },
                            { system: "soc", label: "Endpoints" },
                            { system: "ndr", label: "C2 blocked" },
                            { system: "ztna", label: "Restricted" },
                        ]}
                    />
                </div>
            </Card>

            {/* ── Campaigns Table (original) ──────────────────────────── */}
            <Subheading className="mt-10">{t("phishing.campaigns")}</Subheading>
            <Table className="mt-4" dense>
                <TableHead>
                    <TableRow>
                        <TableHeader>Event</TableHeader>
                        <TableHeader>{t("phishing.name")}</TableHeader>
                        <TableHeader>Severity</TableHeader>
                        <TableHeader>Correlated</TableHeader>
                        <TableHeader />
                    </TableRow>
                </TableHead>
                <TableBody>
                    {phishEvents.map((e) => (
                        <TableRow key={e.id}>
                            <TableCell className="font-mono text-zinc-500">{e.id}</TableCell>
                            <TableCell className="font-medium">{nameMap[e.id] ?? e.title}</TableCell>
                            <TableCell>
                                <Badge
                                    color={
                                        e.severity === "critical"
                                            ? "red"
                                            : e.severity === "high"
                                              ? "orange"
                                              : e.severity === "medium"
                                                ? "yellow"
                                                : "zinc"
                                    }
                                >
                                    {e.severity}
                                </Badge>
                            </TableCell>
                            <TableCell>
                                <CorrelationBadges systems={e.correlated} onNavigate={onNavigate} />
                            </TableCell>
                            <TableCell>
                                <button onClick={() => onInvestigate(e)} className="text-zinc-400 hover:text-blue-600">
                                    <EyeIcon className="h-4 w-4" />
                                </button>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </>
    );
}
